import { isEqual, partition } from 'underscore';
import { AUDIO_FILE, VIDEO_FILE } from '@shoflo/polymer';
import { logProgramChange } from '@shoflo/studio-redux/utils';
import { batch } from 'react-redux';
import { MAX_CAMERA_COUNT } from '@studio/program/utils/scene';
import { sceneBuilder } from '@studio/program/sceneBuilder/sceneBuilder';
import {
  getStartedOn,
  isCameraPresenter,
  attachAudioPresenterSources,
  UNASSIGNED
} from 'utils/broadcastSourceHelper';

import { isAudioSource, orderSources } from '../utils';
import setProgram from '../program/setProgram';
import setSceneLayout from '../program/setSceneLayout';
import { setMultipleDesiredAttendeeMuteStatuses } from '@shoflo/studio-redux/actions';
import clearStagedSources from './clearStagedSources';
import updateSource from './updateSource';
import { StudioLogger } from '@studio/logger/StudioLogger';
import { getCameraPresenterSources } from '@studio/sources/getSources';

const logger = new StudioLogger('program-update');

const isMediaSource = source => [AUDIO_FILE, VIDEO_FILE].includes(source.type);

export default () => {
  return (dispatch, getState) => {
    const {
      sceneLayout,
      sceneLayers,
      banners,
      attendeeMuteStatus,
      broadcastSources: { sources: broadcastSources },
      stagedSources: { sources: stagedSources, sceneLayout: stagedLayout },
      featuredPresenter: featuredPresenterId,
      broadcastSettings: { dragAndDrop, autoMutePresenters },
      program,
      serverTimeDelta,
      meeting: { meetingResponse },
      brandingSettings
    } = getState();

    const combinedLayers = [...sceneLayers, ...banners];

    const layout = stagedLayout || sceneLayout;

    const stagedSourceIds = Object.keys(stagedSources).filter(
      sourceId => stagedSources[sourceId]
    );

    const sourcesToUpdate = [];

    const allStagedSources = broadcastSources
      .filter(bs => [...stagedSourceIds].includes(bs._id))
      .map(source => {
        return {
          ...source,
          featured: source._id === featuredPresenterId
        };
      })
      .map(source => {
        if (isMediaSource(source)) {
          const wasInProgram =
            program.layers &&
            program.layers.filter(ls => ls.source._id === source._id).length >
              0;
          // if this was not in the program we set seek to 0 and startedOn
          if (!wasInProgram) {
            source = {
              ...source,
              value: {
                ...source.value,
                seek: 0,
                isPlaying: true,
                startedOn: getStartedOn(true, serverTimeDelta)
              }
            };
          }
          // if it is an audio source we need set hidden to false so that the
          // scene builder does not exclude it
          if (isAudioSource(source)) source.hidden = false;
          sourcesToUpdate.push(source);
          return source;
        }
        return source;
      });

    const [allStagedCamSources, restStagedSources] = partition(
      allStagedSources,
      isCameraPresenter
    );
    // slice only 10 cam sources
    const stagedCamSources = allStagedCamSources.slice(
      0,
      Math.min(allStagedCamSources.length, MAX_CAMERA_COUNT)
    );

    const sources = [...stagedCamSources, ...restStagedSources];

    const meetingId = meetingResponse?.Meeting?.MeetingId;

    const camSourcesInProgram = getCameraPresenterSources(program.layers);

    const stagedCamSourcesIds = stagedCamSources.map(source => source._id);

    Object.entries(camSourcesInProgram).forEach(([srcID, src]) => {
      if (!stagedCamSourcesIds.includes(srcID)) {
        const logPayload = {
          'studio.actionName': 'Take Staged Sources Live',
          'studio.attendeeTileStatus': 'OFF-STAGE',
          'studio.tileAttendeeId': src.value,
          title: `ProgramUpdate: Attendee ${src.value} in meeting ${meetingId} is removed from the stage`
        };

        logger.info(logPayload.title, logPayload);
      }
    });

    stagedCamSources
      .filter(source => !camSourcesInProgram[source._id])
      .forEach(src => {
        const logPayload = {
          'studio.actionName': 'Take Staged Sources Live',
          'studio.attendeeTileStatus': 'ON-STAGE',
          'studio.tileAttendeeId': src.value,
          title: `ProgramUpdate: Attendee ${src.value} in meeting ${meetingId} is added to the stage`
        };

        logger.info(logPayload.title, logPayload);
      });

    /** Audio/Video sources to make update */
    batch(() => {
      sourcesToUpdate.forEach(source => {
        dispatch(
          updateSource(source._id, {
            value: source.value,
            hidden: source.hidden
          })
        );
      });
    });

    const sceneSources = dragAndDrop ? orderSources(sources) : sources;

    /**
     * When taking sources live we attach audioPresenters for all unmuted camera sources.
     * We use this next unmuted attendees state only when autoMutePresenters setting is enabled otherwise maintain status quo
     */
    const nextMuteStatuses = autoMutePresenters
      ? getAttendeesNextMuteStatuses(sources, broadcastSources)
      : attendeeMuteStatus;

    const withAudioPresenterSources = attachAudioPresenterSources(
      sceneSources,
      nextMuteStatuses
    );

    const scene = sceneBuilder(
      layout,
      withAudioPresenterSources,
      combinedLayers,
      {},
      {},
      brandingSettings?.fonts
    );

    /** Update all staged attendees mute status */
    autoMutePresenters &&
      dispatch(setMultipleDesiredAttendeeMuteStatuses(nextMuteStatuses));

    dispatch(setProgram(scene));
    logProgramChange(getState, 'takeStagedSourcesLive action');

    if (!isEqual(sceneLayout, stagedLayout)) {
      dispatch(setSceneLayout(layout));
    }

    // Finally clear all selections
    dispatch(clearStagedSources());
  };
};

function getAttendeeIds(sources) {
  return sources
    .filter(source => isCameraPresenter(source))
    .map(source => {
      return source?.value === UNASSIGNED
        ? `${UNASSIGNED}-${source?._id}`
        : source?.value;
    });
}

/** We want to unmute all staged sources and mute all other sources */
function getAttendeesNextMuteStatuses(stagedSources, broadcastSources) {
  const stagedAttendeeIds = getAttendeeIds(stagedSources);
  const allAttendeeIds = getAttendeeIds(broadcastSources);

  const nextMuteStatuses = allAttendeeIds.reduce((memo, attendeeId) => {
    const isMuted = !stagedAttendeeIds.includes(attendeeId);
    return { ...memo, [attendeeId]: isMuted };
  }, {});

  return nextMuteStatuses;
}
